/**
 * Bring pages in from a file: PowerPoint (.pptx → editable Free Style pages), PDF (one page per PDF page, as a
 * picture), pictures (one page each) and our own deck JSON. Pictures are uploaded to the deck's media store;
 * nothing is saved until the caller adds the pages to a deck.
 */
import { coworkApi, API_BASE_URL } from '@/cowork/omniad/api/coworkApi';
import { newId } from '@/cowork/omniad/components/slides/elements';
import { parsePptx, PPTX_MEDIA } from './pptxImport';

type Slide = Record<string, any>;
export interface ImportResult { title: string; slides: Slide[]; settings?: Record<string, unknown>; skipped: string[] }
export type Progress = (label: string) => void;
/** Upload files; returns file name → URL for the ones that were stored. */
export type Uploader = (files: File[]) => Promise<Map<string, string>>;

export const IMPORT_ACCEPT = '.pptx,.pdf,.png,.jpg,.jpeg,.webp,.gif,.json,application/pdf,application/vnd.openxmlformats-officedocument.presentationml.presentation,image/*';
export const IMPORT_FORMATS = 'PowerPoint (.pptx), PDF, images or a deck file (.json)';
const MAX_PDF_PAGES = 120;

const abs = (u: string) => (u.startsWith('/') ? `${API_BASE_URL}${u}` : u);

/** The deck media store, ten files per request (the server's limit), matched back by file name. */
export const uploadToDeckMedia: Uploader = async (files) => {
  const out = new Map<string, string>();
  for (let i = 0; i < files.length; i += 10) {
    const r = await coworkApi.presentations.uploadMedia(files.slice(i, i + 10));
    for (const f of r.files) out.set(f.name, abs(f.url));
  }
  return out;
};

/** A page that shows one picture, whole (letterboxed on white when its shape is not 16:9). */
export function pictureSlide(url: string, alt = ''): Slide {
  return { type: 'free', bgColor: '#FFFFFF', beatlyMark: false, _from: { import: 'picture' }, elements: [{ id: newId(), kind: 'image', x: 0, y: 0, w: 100, h: 100, z: 1, media: { url, kind: 'image', fit: 'contain', alt } }] };
}

const extOf = (f: File) => (f.name.split('.').pop() || '').toLowerCase();
export function importKind(f: File): 'pptx' | 'pdf' | 'image' | 'json' | 'ppt' | 'keynote' | null {
  const ext = extOf(f);
  if (ext === 'pptx' || f.type === 'application/vnd.openxmlformats-officedocument.presentationml.presentation') return 'pptx';
  if (ext === 'pdf' || f.type === 'application/pdf') return 'pdf';
  if (['png', 'jpg', 'jpeg', 'webp', 'gif'].includes(ext) || f.type.startsWith('image/')) return 'image';
  if (ext === 'json' || f.type === 'application/json') return 'json';
  if (ext === 'ppt') return 'ppt';
  if (ext === 'key') return 'keynote';
  return null;
}

/** Render each PDF page to a JPEG (1920 px wide) in the browser. */
async function pdfPages(file: File, onProgress: Progress): Promise<{ title: string; pages: File[]; skipped: string[] }> {
  const pdfjs = await import('pdfjs-dist');
  const worker = (await import('pdfjs-dist/build/pdf.worker.min.mjs?url')).default;
  pdfjs.GlobalWorkerOptions.workerSrc = worker;
  const task = pdfjs.getDocument({ data: new Uint8Array(await file.arrayBuffer()) });
  const doc = await task.promise;
  const meta = await doc.getMetadata().catch(() => null);
  const title = ((meta?.info as { Title?: string } | undefined)?.Title || '').trim();
  const n = Math.min(doc.numPages, MAX_PDF_PAGES);
  const pages: File[] = [];
  for (let i = 1; i <= n; i++) {
    onProgress(`Reading page ${i} of ${n}…`);
    const page = await doc.getPage(i);
    const base = page.getViewport({ scale: 1 });
    const viewport = page.getViewport({ scale: 1920 / base.width });
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(viewport.width); canvas.height = Math.round(viewport.height);
    const ctx = canvas.getContext('2d'); if (!ctx) throw new Error('This browser cannot draw PDF pages.');
    ctx.fillStyle = '#FFFFFF'; ctx.fillRect(0, 0, canvas.width, canvas.height);
    await page.render({ canvas, canvasContext: ctx, viewport }).promise;
    const blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, 'image/jpeg', 0.9));
    if (blob) pages.push(new File([blob], `pdf-page-${String(i).padStart(3, '0')}.jpg`, { type: 'image/jpeg' }));
    page.cleanup();
  }
  await doc.cleanup(); await task.destroy();
  return { title, pages, skipped: doc.numPages > n ? [`pages after ${n}`] : [] };
}

/** Swap `pptx-media:` placeholders for uploaded URLs; drop pictures that did not upload. */
function resolveMedia(slides: Slide[], urlOf: (path: string) => string | undefined): Slide[] {
  return slides.map((s) => ({
    ...s,
    elements: (s.elements || []).flatMap((e: any) => {
      if (e.kind !== 'image' || !String(e.media?.url || '').startsWith(PPTX_MEDIA)) return [e];
      const url = urlOf(String(e.media.url).slice(PPTX_MEDIA.length));
      return url ? [{ ...e, media: { ...e.media, url } }] : [];
    }),
  }));
}

export async function importDeckFile(file: File, opts: { upload?: Uploader; onProgress?: Progress } = {}): Promise<ImportResult> {
  const upload = opts.upload || uploadToDeckMedia;
  const onProgress = opts.onProgress || (() => {});
  const baseName = file.name.replace(/\.[^.]+$/, '');
  switch (importKind(file)) {
    case 'pptx': {
      onProgress('Reading the PowerPoint…');
      const r = await parsePptx(file, file.name);
      if (r.media.size) {
        onProgress(`Uploading ${r.media.size} picture${r.media.size === 1 ? '' : 's'}…`);
        const urls = await upload([...r.media.values()]);
        const byPath = new Map([...r.media.entries()].map(([path, f]) => [path, urls.get(f.name)]));
        const missing = [...byPath.values()].filter((u) => !u).length;
        return { title: r.title, slides: resolveMedia(r.slides, (p) => byPath.get(p)), skipped: [...r.skipped, ...(missing ? [`${missing} pictures that did not upload`] : [])] };
      }
      return { title: r.title, slides: r.slides, skipped: r.skipped };
    }
    case 'pdf': {
      const { title, pages, skipped } = await pdfPages(file, onProgress);
      if (!pages.length) throw new Error('The PDF has no pages.');
      onProgress(`Uploading ${pages.length} page${pages.length === 1 ? '' : 's'}…`);
      const urls = await upload(pages);
      const slides = pages.map((p, i) => (urls.get(p.name) ? pictureSlide(urls.get(p.name)!, `Page ${i + 1}`) : null)).filter((s): s is Slide => !!s);
      if (!slides.length) throw new Error('The pages could not be uploaded.');
      return { title: title || baseName, slides, skipped };
    }
    case 'image': {
      onProgress('Uploading the picture…');
      const urls = await upload([file]);
      const url = urls.get(file.name);
      if (!url) throw new Error('The picture could not be uploaded.');
      return { title: baseName, slides: [pictureSlide(url, baseName)], skipped: [] };
    }
    case 'json': {
      const parsed = JSON.parse(await file.text()) as { title?: string; slides?: Slide[]; settings?: Record<string, unknown> };
      if (!Array.isArray(parsed.slides) || !parsed.slides.length) throw new Error('The file has no "slides" array');
      return { title: (parsed.title || baseName).trim(), slides: parsed.slides, settings: parsed.settings, skipped: [] };
    }
    case 'ppt': throw new Error('Old PowerPoint files (.ppt) cannot be read. Open it in PowerPoint and save as .pptx, or export a PDF.');
    case 'keynote': throw new Error('Keynote files cannot be read. In Keynote, File → Export To → PowerPoint (or PDF), then import that.');
    default: throw new Error(`This file type is not supported. Import ${IMPORT_FORMATS}.`);
  }
}

/** Several files at once (e.g. a folder of pictures): pages in file order. */
export async function importDeckFiles(files: File[], opts: { upload?: Uploader; onProgress?: Progress } = {}): Promise<ImportResult> {
  if (files.length === 1) return importDeckFile(files[0], opts);
  const out: ImportResult = { title: files[0]?.name.replace(/\.[^.]+$/, '') || 'Imported', slides: [], skipped: [] };
  for (const [i, f] of files.entries()) {
    opts.onProgress?.(`File ${i + 1} of ${files.length}: ${f.name}`);
    const r = await importDeckFile(f, opts);
    out.slides.push(...r.slides); out.skipped.push(...r.skipped);
  }
  return out;
}
